import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Input, Select, Button, Space, Tag, DatePicker, Modal, Descriptions, Row, Col, Statistic, Upload, InputNumber, message, Form, Tooltip, Segmented } from 'antd';
import { SearchOutlined, ReloadOutlined, UploadOutlined, InboxOutlined, PlusOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { yuan, percent, formatMargin, fmtWeight, zoneTagColor } from '../utils/format.js';
import { apiFetch, apiUpload, notifyDataChanged, onDataChanged } from '../utils/api.js';

const { RangePicker } = DatePicker;
const { Dragger } = Upload;
import dayjs from 'dayjs';

// 规格编码展示：像快递单号列一样用 Tag 自动换行展示多个编码；点击编辑按钮进入输入框（支持逗号分隔多编码），保存后恢复展示
function SpecCodeCell({ record, onRelink }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(record.spec_code);
  const [error, setError] = React.useState(!String(record.spec_code || '').trim());
  const timerRef = React.useRef(null);
  // 规格编码在输入完成后（回车 / 点击别处失焦）才匹配更新；输入过程中不自动保存
  const save = async (v) => {
    const code = String(v == null ? value : v).trim();
    if (code === record.spec_code) {
      if (!code) { setError(true); return; } // 编码未变化且为空：保持红框提示待填写
      setError(false); setEditing(false);
      return;
    }
    const codes = code.replace(/[，、]/g, ',').split(',').map(s => s.trim()).filter(Boolean);
    if (!codes.length) { setError(true); return; }
    const bad = codes.find(c => !/\d+\s*$/.test(c));
    if (bad) { setError(true); message.error(`编码「${bad}」缺少数量后缀（如 HB-0001-蓝-1000），无法计算实发数量`); return; }
    try {
      const res = await onRelink(record.platform_order_no, code);
      if (res.success) { setError(false); setEditing(false); message.success('已自动获取商品信息'); }
      else { setError(true); message.error(res.error || '更新失败'); }
    } catch { setError(true); message.error('保存失败，请重试'); }
  };
  const onChange = (e) => {
    setValue(e.target.value);
    setError(!e.target.value.trim()); // 空输入 → 红框提示待填写
  };
  React.useEffect(() => () => clearTimeout(timerRef.current), []);
  if (editing) {
    return (
      <Input size="small" autoFocus value={value} status={error ? 'error' : undefined}
        placeholder={error ? '请填写规格编码' : undefined}
        onChange={onChange} onBlur={() => save()} onPressEnter={() => save()}
        title="多个编码用逗号或顿号分隔，回车或点击别处后自动匹配更新" />
    );
  }
  const codes = String(record.spec_code || '').split(/[,，、]/).map(s => s.trim()).filter(Boolean);
  if (!codes.length) {
    return (
      <Space size={4} wrap>
        <Tag color="#CF1322">空</Tag>
        <Button size="small" type="link" icon={<EditOutlined />} onClick={() => { setValue(''); setEditing(true); }} />
      </Space>
    );
  }
  const unreg = record.unregistered_codes || [];
  return (
    <Space size={4} wrap>
      {codes.map((c, i) => {
        const isUnreg = unreg.includes(c);
        const color = isUnreg ? '#722ED1' : (i === 0 ? '#D85A30' : undefined);
        return <Tag key={i} color={color} style={i === 0 && !isUnreg ? { fontWeight: 500 } : undefined}>{c}</Tag>;
      })}
      <Button size="small" type="link" icon={<EditOutlined />} onClick={() => { setValue(record.spec_code); setEditing(true); }} />
    </Space>
  );
}

// 件数可手动修改：点击进入编辑，输入后回车/失焦保存，联动重算实发/成本/运费/利润
function QtyCell({ record, onQtyChange }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(record.order_quantity);
  const save = async () => {
    const n = Number(val);
    if (!(n > 0)) { message.warning('件数必须大于 0'); return; }
    setEditing(false);
    if (n === Number(record.order_quantity)) return;
    try {
      const res = await onQtyChange(record.platform_order_no, n);
      if (!res.success) message.error(res.error || '保存失败');
    } catch { message.error('保存失败，请重试'); }
  };
  if (editing) {
    return (
      <InputNumber size="small" min={1} value={val} autoFocus
        onChange={setVal} onBlur={save} onPressEnter={save} style={{ width: 64 }} />
    );
  }
  return (
    <span style={{ cursor: 'pointer', userSelect: 'none' }} title="点击修改件数"
      onClick={() => { setVal(record.order_quantity); setEditing(true); }}>
      {record.order_quantity}<EditOutlined style={{ fontSize: 10, color: '#8c8c8c', marginLeft: 4 }} />
    </span>
  );
}

// 成本/运费可手动调整：点击进入编辑（空值=恢复自动计算）；手动值橙色标记，利润联动重算
function MoneyCell({ record, field, onSave }) {
  const isCost = field === 'cost';
  const manual = isCost ? record.manual_cost != null : record.manual_shipping != null;
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(isCost ? record.product_cost : record.shipping_fee);
  const save = async () => {
    setEditing(false);
    const cur = Number(record[isCost ? 'product_cost' : 'shipping_fee']);
    if (val !== '' && val != null && Number(val) === cur && !manual) return; // 值未变且非手动：跳过
    try {
      const res = await onSave(record.platform_order_no, field, (val === '' || val == null) ? '' : Number(val));
      if (!res.success) message.error(res.error || '保存失败');
    } catch { message.error('保存失败，请重试'); }
  };
  if (editing) {
    return (
      <InputNumber size="small" min={0} value={val} autoFocus precision={2}
        onChange={setVal} onBlur={save} onPressEnter={save}
        placeholder="留空=自动" style={{ width: 78 }} />
    );
  }
  return (
    <span style={{ cursor: 'pointer', userSelect: 'none', color: manual ? '#EF9F27' : undefined, fontWeight: manual ? 600 : undefined }}
      title={manual ? `手动调整值（${isCost ? '成本' : '运费'}），点击可改或留空恢复自动` : '点击手动调整，留空恢复自动'}
      onClick={() => { setVal(record[isCost ? 'product_cost' : 'shipping_fee']); setEditing(true); }}>
      ¥{yuan(record[isCost ? 'product_cost' : 'shipping_fee'])}<EditOutlined style={{ fontSize: 10, color: '#8c8c8c', marginLeft: 4 }} />
    </span>
  );
}

// 商品名称多值展示：像快递单号列一样用 Tag 自动换行展示多个名称（未登记标红；单名称正常显示）
function MultiNameCell({ names }) {
  const list = (names || []).filter(Boolean);
  if (!list.length) return '-';
  return (
    <Space size={4} wrap>
      {list.map((n, i) => <Tag key={i} color={n === '未登记' ? 'red' : '#378ADD'}>{n}</Tag>)}
    </Space>
  );
}

export default function Orders() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ keyword: '', courier: 'all', region: 'all', product: 'all', shop: 'all', channel: 'all', startDate: '', endDate: '', status: 'all' });
  const [kwDraft, setKwDraft] = useState(''); // 搜索框草稿值（输入不触发请求，回车/查询按钮才生效）
  const [filterOptions, setFilterOptions] = useState({ couriers: [], regions: [], products: [], provinces: [], signStatuses: [], shops: [] });
  const [detailModal, setDetailModal] = useState(null);
  const [logisticsDetail, setLogisticsDetail] = useState(null);
  // 额外快递单号管理
  const [trackingModal, setTrackingModal] = useState(null);
  const [newTracking, setNewTracking] = useState('');
  // 上传发货订单相关
  const [uploadModal, setUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [anomalyModal, setAnomalyModal] = useState(null); // 导入异常报告弹窗
  const [importMode, setImportMode] = useState('daily'); // daily=每日导入(扣库存) history=历史补录(不扣库存)
  const [importChannel, setImportChannel] = useState('pdd'); // pdd=拼多多/微信导入 taobao=淘宝发货单导入
  // 手动录入订单（微信客户导入）
  const [manualModal, setManualModal] = useState(false);
  const [manualForm] = Form.useForm();
  const shippingMode = Form.useWatch('shipping_mode', manualForm); // auto=自动算运费 manual=手动填运费
  const [prodOpts, setProdOpts] = useState([]); // 商品选项
  const [manualSaving, setManualSaving] = useState(false);
  // 订单发货（待发货 → 已发货）
  const [shipTarget, setShipTarget] = useState(null);
  const [shipForm] = Form.useForm();
  const [shipSaving, setShipSaving] = useState(false);

  const loadProdOpts = useCallback(() => {
    apiFetch('/api/products').then(r => r.json()).then(d => {
      const set = {};
      for (const p of (d.data || [])) {
        const list = p.children && p.children.length ? p.children : (p.spec_code ? [p] : []);
        for (const c of list) {
          const raw = String(c.spec_code || '').trim();
          if (raw && !set[raw]) set[raw] = p.product_name || raw;
        }
      }
      setProdOpts(Object.entries(set).map(([code, name]) => ({ value: code, label: `${code}（${name}）` })));
    }).catch(() => {});
  }, []);
  useEffect(() => { loadProdOpts(); }, [loadProdOpts]);
  useEffect(() => onDataChanged(() => { loadProdOpts(); }), [loadProdOpts]);

  // 提交手动录入订单（微信客户，共用库存）
  const submitManual = async () => {
    try {
      const v = await manualForm.validateFields();
      setManualSaving(true);
      const res = await apiFetch('/api/orders/manual', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spec_items: (v.spec_codes || []).map(c => ({ spec_code: c })),
          piece_qty: v.piece_qty,
          order_price: v.order_price,
          shipping_date: v.shipping_date ? v.shipping_date.format('YYYY-MM-DD') : undefined,
          shop: v.shop, region: v.region, courier_company: v.courier_company,
          tracking_no: v.tracking_no, customer_name: v.customer_name,
          address: v.address, remark: v.remark, is_dropship: v.is_dropship,
          // 手动填写运费时传数值（0=客户付运费），自动计算时不传（undefined 会被 JSON 省略）
          manual_shipping: v.shipping_mode === 'manual' ? (v.manual_shipping ?? 0) : undefined,
        }),
      }).then(r => r.json());
      setManualSaving(false);
      if (res.success) {
        message.success(`已录入 ${res.data.platform_order_no}（${res.data.product_name}）· 利润 ¥${yuan(res.data.profit)}，库存已扣减`);
        setManualModal(false); manualForm.resetFields(); loadData(); notifyDataChanged();
      } else message.error(res.error || '录入失败');
    } catch (e) { setManualSaving(false); if (e && e.errorFields) return; message.error('录入失败'); }
  };

  // 提交发货（待发货 → 已发货，扣库存 + 进财务）
  const submitShip = async () => {
    try {
      const v = await shipForm.validateFields();
      setShipSaving(true);
      const res = await apiFetch('/api/orders/ship', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform_order_no: shipTarget.platform_order_no,
          courier_company: v.courier_company,
          tracking_no: v.tracking_no,
          shipping_date: v.shipping_date ? v.shipping_date.format('YYYY-MM-DD') : undefined,
        }),
      }).then(r => r.json());
      setShipSaving(false);
      if (res.success) {
        message.success('已发货，订单进入财务计算');
        setShipTarget(null); shipForm.resetFields(); loadData(); notifyDataChanged();
      } else message.error(res.error || '发货失败');
    } catch (e) { setShipSaving(false); if (e && e.errorFields) return; message.error('发货失败'); }
  };

  const loadFilters = useCallback(() => {
    apiFetch('/api/filters').then(r => r.json()).then(setFilterOptions);
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, pageSize, ...filters });
    apiFetch(`/api/orders?${params}`)
      .then(r => r.json())
      .then(d => { setData(d.data); setTotal(d.total); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, pageSize, filters]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadData(); }, [loadData]);
  // 数据变更（其它页面改编码/导入/商品成本修改）→ 静默刷新（保留当前筛选状态）
  useEffect(() => onDataChanged(() => { loadData(); }), [loadData]);

  // 查询：把搜索框当前输入同步进 filters 再触发加载（点击查询按钮也能正确执行）
  const handleSearch = () => { setPage(1); setFilters(f => ({ ...f, keyword: kwDraft })); };
  const handleReset = () => {
    setKwDraft('');
    setFilters({ keyword: '', courier: 'all', region: 'all', product: 'all', shop: 'all', channel: 'all', startDate: '', endDate: '', status: 'all' });
    setPage(1);
  };

  const showDetail = (record) => {
    setDetailModal(record);
    setLogisticsDetail(null);
    apiFetch(`/api/orders/${record.tracking_no}/logistics`)
      .then(r => r.json())
      .then(d => setLogisticsDetail(d.data[0] || null));
  };

  // 额外快递单号
  const updateLocalExtras = (orderNo, extraList) => {
    setData(prev => prev.map(o => (o.platform_order_no === orderNo ? { ...o, extra_tracking_nos: extraList } : o)));
  };
  const addExtraTracking = async () => {
    const no = newTracking.trim();
    if (!no) { message.warning('请输入快递单号'); return; }
    const res = await apiFetch('/api/orders/extra-tracking', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform_order_no: trackingModal.platform_order_no, tracking_no: no }),
    }).then(r => r.json());
    if (res.success) { updateLocalExtras(trackingModal.platform_order_no, res.data); setNewTracking(''); }
    else message.error(res.error || '添加失败');
  };
  const removeExtraTracking = async (no) => {
    const res = await apiFetch(`/api/orders/extra-tracking?platform_order_no=${encodeURIComponent(trackingModal.platform_order_no)}&tracking_no=${encodeURIComponent(no)}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) updateLocalExtras(trackingModal.platform_order_no, res.data);
    else message.error(res.error || '删除失败');
  };

  // 修改规格编码 → 联动重算商品名称/成本/重量/运费并保存
  const relinkOrder = async (orderNo, code) => {
    const res = await apiFetch('/api/orders/relink', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform_order_no: orderNo, spec_code: code }),
    }).then(r => r.json());
    if (res.success) {
      setData(prev => prev.map(o => (o.platform_order_no === orderNo ? { ...o, ...res.data } : o)));
      notifyDataChanged(); // 通知其它常驻页面同步刷新（商品成本/统计等）
    }
    return res;
  };

  // 手动修改件数：重算实发/成本/运费/利润并同步其它页面
  const changeQty = async (orderNo, qty) => {
    const res = await apiFetch('/api/orders/quantity', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform_order_no: orderNo, order_quantity: qty }),
    }).then(r => r.json());
    if (res.success) {
      setData(prev => prev.map(o => (o.platform_order_no === orderNo ? { ...o, ...res.data } : o)));
      notifyDataChanged(); // 通知其它常驻页面同步刷新
    }
    return res;
  };

  // 手动调整成本/运费：利润联动重算并同步其它页面（''=恢复自动计算）
  const saveManualField = async (orderNo, field, value) => {
    const body = { platform_order_no: orderNo };
    body[field] = value;
    const res = await apiFetch('/api/orders/manual-fields', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());
    if (res.success) {
      setData(prev => prev.map(o => (o.platform_order_no === orderNo ? { ...o, ...res.data } : o)));
      notifyDataChanged();
    }
    return res;
  };

  // 忽略/恢复订单异常标记
  const toggleIgnore = async (record, ignored) => {
    const res = await apiFetch('/api/orders/anomaly-ignore', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform_order_no: record.platform_order_no, ignored }),
    }).then(r => r.json());
    if (res.success) {
      setData(prev => prev.map(o => (o.platform_order_no === record.platform_order_no ? { ...o, anomaly_ignored: ignored } : o)));
    } else {
      message.error(res.error || '操作失败');
    }
  };

  const columns = [
    { title: '平台订单号', dataIndex: 'platform_order_no', width: 215, fixed: 'left',
      render: (v, r) => (
        <Space size={4}>
          <a onClick={() => showDetail(r)}>{v}</a>
          {r.is_wechat && !(r.channel === 'taobao' || /淘宝|天猫/.test(String(r.shop || ''))) && <Tag color="#52C41A">微信</Tag>}
          {(r.channel === 'taobao' || /淘宝|天猫/.test(String(r.shop || ''))) && <Tag color="#EB2F96">淘宝</Tag>}
          {r.is_dropship && <Tag color="#FA8C16">整单代发</Tag>}
          {!r.is_dropship && r.has_line_dropship && <Tag color="#FA8C16">含代发</Tag>}
          {r.is_custom && r.group_count > 1 && <Tag color="#EF9F27">定制·同单{r.group_count}行</Tag>}
          {r.anomaly_ignored
            ? <Tooltip title={(r.anomalies || []).join('；') || '已忽略'}><Tag color="#d9d9d9">已忽略</Tag></Tooltip>
            : (
              <>
                {r.anomaly_level === 'red' && <Tooltip title={(r.anomalies || []).join('；')}><Tag color="#CF1322">异常</Tag></Tooltip>}
                {r.anomaly_level === 'purple' && <Tooltip title={(r.anomalies || []).join('；')}><Tag color="#722ED1">未登记</Tag></Tooltip>}
                {r.anomaly_level === 'yellow' && <Tooltip title={(r.anomalies || []).join('；')}><Tag color="#FAAD14">警告</Tag></Tooltip>}
                {r.anomaly_level && <Button size="small" type="link" style={{ padding: 0, height: 20 }} onClick={() => toggleIgnore(r, true)}>忽略</Button>}
              </>
            )
          }
        </Space>
      ) },
    { title: '店铺', dataIndex: 'shop', width: 170, ellipsis: true,
      render: v => v ? <span style={{ color: '#D4537E' }}>{v}</span> : '-' },
    { title: '发货状态', dataIndex: 'shipping_status', width: 110,
      render: (v, r) => r.shipping_date
        ? <span>{r.shipping_date}</span>
        : <a style={{ color: 'var(--brand)', fontWeight: 600 }} onClick={() => { shipForm.resetFields(); shipForm.setFieldsValue({ shipping_date: dayjs(), courier_company: r.courier_company || '', tracking_no: r.tracking_no || '' }); setShipTarget(r); }}>待发货 · 点此发货</a> },
    { title: '区域', dataIndex: 'region_view', width: 70,
      render: v => <Tag color={zoneTagColor(v)}>{v}</Tag> },
    { title: '下单地区', dataIndex: 'order_region', width: 100 },
    { title: '快递公司', dataIndex: 'courier_company', width: 90 },
    { title: '快递单号', dataIndex: 'tracking_no', width: 190,
      render: (v, r) => {
        // 多单号（换行/逗号分隔）逐个独立成行显示
        const tns = String(v || '').split(/\n|,|，/).map(s => s.trim()).filter(Boolean);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {tns.map((t, i) => <span key={i} style={{ fontSize: 12, lineHeight: '18px', color: '#378ADD', wordBreak: 'break-all' }}>{t}</span>)}
            {(r.extra_tracking_nos || []).map(t => <span key={t} style={{ fontSize: 12, lineHeight: '18px', color: '#8c8c8c', wordBreak: 'break-all' }}>{t}</span>)}
            <Button size="small" type="link" icon={<PlusOutlined />} style={{ padding: 0, height: 20 }} onClick={() => { setNewTracking(''); setTrackingModal(r); }} />
          </div>
        );
      } },
    { title: '商品名称', dataIndex: 'product_name', width: 190,
      render: (v, r) => <MultiNameCell names={(r.spec_names && r.spec_names.length) ? r.spec_names : String(v || '').split(',').filter(Boolean)} /> },
    { title: '规格编码', dataIndex: 'spec_code', width: 200,
      render: (v, r) => <SpecCodeCell record={r} onRelink={relinkOrder} /> },
    { title: '实收价', dataIndex: 'order_price', width: 90, align: 'right',
      render: v => <span style={{ fontWeight: 600, color: 'var(--brand)' }}>¥{yuan(v)}</span> },
    { title: '件数', dataIndex: 'order_quantity', width: 70, align: 'right',
      render: (v, r) => <QtyCell record={r} onQtyChange={changeQty} /> },
    { title: '实发数量', dataIndex: 'product_quantity', width: 85, align: 'right' },
    { title: '商品重量', dataIndex: 'weight_ref', width: 100, align: 'right',
      render: (v, r) => (r.product_quantity && r.unit_weight_g ? fmtWeight(Number(r.unit_weight_g) * r.product_quantity) : fmtWeight(v)) },
    { title: '成本', dataIndex: 'product_cost', width: 100, align: 'right',
      render: (v, r) => <MoneyCell record={r} field="cost" onSave={saveManualField} /> },
    { title: '运费', dataIndex: 'shipping_fee', width: 90, align: 'right',
      render: (v, r) => <MoneyCell record={r} field="shipping" onSave={saveManualField} /> },
    { title: '辅材', dataIndex: 'packaging_cost', width: 80, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '服务费', dataIndex: 'tech_fee', width: 90, align: 'right',
      render: v => <span style={{ color: '#8B5CF6' }}>¥{yuan(v)}</span> },
    { title: '利润', dataIndex: 'profit', width: 90, align: 'right',
      render: (v, r) => <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan((v || 0) - (r.tech_fee || 0))}</span> },
    { title: '毛利率', dataIndex: 'profit_margin', width: 90, align: 'right',
      render: (v, r) => {
        const price = Number(r.order_price) || 0;
        const netMargin = price > 0 ? ((Number(r.profit) || 0) - (Number(r.tech_fee) || 0)) / price : 0;
        const m = formatMargin(netMargin);
        return <span style={{ color: m.color }}>{m.text}</span>;
      } },
  ];

  const handleUpload = async () => {
    if (!uploadFile) { message.warning('请先选择 Excel 文件'); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      // 渠道 + 模式 → 接口映射
      const endpointMap = {
        pdd_daily: '/api/import-daily',
        pdd_history: '/api/import-history',
        taobao_daily: '/api/import-taobao',
        taobao_history: '/api/import-taobao-history',
      };
      const endpoint = endpointMap[`${importChannel}_${importMode}`] || '/api/import-daily';
      const res = await apiUpload(endpoint, uploadFile);
      if (res.success) {
        message.success(`导入完成：新增 ${res.newOrders} 单，更新 ${res.updatedOrders} 单`);
        if (res.warnings && res.warnings.length) {
          message.warning(`发现 ${res.warnings.length} 个未登记编码（不在商品成本主数据，已标紫色、未新建库存）：${res.warnings.join('、')}`, 8);
        }
        if (res.unmappedSpecs && res.unmappedSpecs.length) {
          message.warning(`有 ${res.unmappedSpecs.length} 个淘宝规格未映射到系统编码，请补充映射：${res.unmappedSpecs.join('、')}`, 8);
        }
        if (res.anomalies && res.anomalies.length) {
          setAnomalyModal(res.anomalies);
        }
        setUploadResult(res);
        loadData();
        notifyDataChanged(); // 通知其它页面同步（商品统计/仪表盘等）
      } else {
        message.error(res.error || '导入失败');
      }
    } catch (e) {
      message.error('导入失败，请重试');
    }
    setUploading(false);
  };

  return (
    <div className="page-container">
      <Card
        style={{ marginBottom: 16 }}
        title={
          <span style={{ fontSize: 15, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 3, height: 15, borderRadius: 2, background: 'var(--brand)', display: 'inline-block' }} />
            订单筛选
          </span>
        }
        extra={
          <Space>
            <Button type="primary" icon={<UploadOutlined />} onClick={() => { setUploadResult(null); setUploadFile(null); setUploadModal(true); }}>上传发货订单</Button>
            <Button icon={<PlusOutlined />} onClick={() => { manualForm.resetFields(); manualForm.setFieldsValue({ shipping_date: dayjs(), shop: '微信客户', piece_qty: 1, shipping_mode: 'auto' }); setManualModal(true); }}>手动录入订单</Button>
          </Space>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr 1.3fr 1.3fr 1.1fr 1fr 1.5fr auto auto', gap: 10, alignItems: 'center' }}>
          <Input.Search
            placeholder="搜索订单号/快递单号/商品"
            allowClear
            style={{ width: '100%' }}
            value={kwDraft}
            onChange={e => setKwDraft(e.target.value)}
            onSearch={v => { setKwDraft(v); setFilters(f => ({ ...f, keyword: v })); }}
          />
          <Select style={{ width: '100%' }} value={filters.courier} onChange={v => setFilters({ ...filters, courier: v })}
            options={[{ value: 'all', label: '全部快递' }, ...filterOptions.couriers.map(c => ({ value: c, label: c }))]} />
          <Select style={{ width: '100%' }} value={filters.region} onChange={v => setFilters({ ...filters, region: v })}
            options={[{ value: 'all', label: '全部区域' }, ...filterOptions.regions.map(r => ({ value: r, label: r }))]} />
          <Select style={{ width: '100%' }} value={filters.product} onChange={v => setFilters({ ...filters, product: v })}
            showSearch optionFilterProp="label"
            options={[{ value: 'all', label: '全部商品' }, { value: '__EMPTY__', label: '空白编码（未填写）' }, ...filterOptions.products.map(p => ({ value: p.spec_code, label: p.product_name }))]} />
          <Select style={{ width: '100%' }} value={filters.shop} onChange={v => setFilters({ ...filters, shop: v })}
            showSearch optionFilterProp="label"
            options={[{ value: 'all', label: '全部店铺' }, ...filterOptions.shops.map(s => ({ value: s, label: s }))]} />
          <Select style={{ width: '100%' }} value={filters.channel} onChange={v => setFilters({ ...filters, channel: v })}
            options={[{ value: 'all', label: '全部渠道' }, { value: 'pdd', label: '拼多多' }, { value: 'wechat', label: '微信' }, { value: 'taobao', label: '淘宝' }]} />
          <Segmented
            value={filters.status}
            onChange={v => setFilters({ ...filters, status: v })}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'pending', label: <span style={{ color: '#FA8C16' }}>待发货</span> },
              { value: 'shipped', label: <span style={{ color: '#3F8600' }}>已发货</span> },
            ]}
          />
          <RangePicker
            format="YYYY年MM月DD日"
            value={[filters.startDate ? dayjs(filters.startDate) : null, filters.endDate ? dayjs(filters.endDate) : null]}
            onChange={(dates) => setFilters({
              ...filters,
              startDate: dates?.[0]?.format('YYYY-MM-DD') || '',
              endDate: dates?.[1]?.format('YYYY-MM-DD') || '',
            })}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        className="orders-table"
        scroll={{ x: 2290 }}
        size="small"
        rowClassName={(r) => (r.is_custom ? 'custom-order-row' : '')}
        pagination={{
          current: page, pageSize, total,
          showSizeChanger: true, showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
      />

      <Modal
        title="订单详情"
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        width={800}
      >
        {detailModal && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="平台订单号" span={2}>{detailModal.platform_order_no}</Descriptions.Item>
              <Descriptions.Item label="店铺">{detailModal.shop}</Descriptions.Item>
              <Descriptions.Item label="发货状态">
                <Space size={4} wrap>
                  <Tag color="#3F8600">{detailModal.shipping_status}</Tag>
                  {detailModal.is_dropship && <Tag color="#FA8C16">整单代发</Tag>}
                  {!detailModal.is_dropship && detailModal.has_line_dropship && <Tag color="#FA8C16">含代发</Tag>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="区域">{detailModal.region_view}</Descriptions.Item>
              <Descriptions.Item label="下单地区">{detailModal.order_region}</Descriptions.Item>
              <Descriptions.Item label="销售月">{detailModal.sales_month}</Descriptions.Item>
              <Descriptions.Item label="发货时间">{detailModal.shipping_date}</Descriptions.Item>
              <Descriptions.Item label="快递公司">{detailModal.courier_company}</Descriptions.Item>
              <Descriptions.Item label="快递单号">{detailModal.tracking_no}</Descriptions.Item>
              <Descriptions.Item label="商品名称" span={2}>{detailModal.product_name}</Descriptions.Item>
              <Descriptions.Item label="规格编码">{detailModal.spec_code}</Descriptions.Item>
              <Descriptions.Item label="订单数量">{detailModal.order_quantity}</Descriptions.Item>
              <Descriptions.Item label="商品数量">{detailModal.product_quantity}</Descriptions.Item>
              <Descriptions.Item label="商品重量">{fmtWeight(Number(detailModal.unit_weight_g) * detailModal.product_quantity || detailModal.weight_ref)}</Descriptions.Item>
              <Descriptions.Item label="订单实收价"><span style={{ color: 'var(--brand)', fontWeight: 600 }}>¥{yuan(detailModal.order_price)}</span></Descriptions.Item>
              <Descriptions.Item label="商品成本">¥{yuan(detailModal.product_cost)}</Descriptions.Item>
              <Descriptions.Item label="运费">¥{yuan(detailModal.shipping_fee)}</Descriptions.Item>
              <Descriptions.Item label="辅材成本">¥{yuan(detailModal.packaging_cost)}</Descriptions.Item>
              <Descriptions.Item label="技术服务费"><span style={{ color: '#8B5CF6' }}>¥{yuan(detailModal.tech_fee)}</span></Descriptions.Item>
              <Descriptions.Item label="利润"><span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan((detailModal.profit || 0) - (detailModal.tech_fee || 0))}</span></Descriptions.Item>
              <Descriptions.Item label="毛利率"><span style={{ color: '#D4537E', fontWeight: 600 }}>{percent((Number(detailModal.order_price) || 0) > 0 ? ((Number(detailModal.profit) || 0) - (Number(detailModal.tech_fee) || 0)) / (Number(detailModal.order_price) || 1) : 0, 3)}%</span></Descriptions.Item>
            </Descriptions>

            {logisticsDetail && (
              <>
                <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600, fontSize: 14 }}>物流信息</div>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="寄件网点">{logisticsDetail.sender_branch}</Descriptions.Item>
                  <Descriptions.Item label="始发地">{logisticsDetail.origin}</Descriptions.Item>
                  <Descriptions.Item label="派件网点">{logisticsDetail.receiver_branch}</Descriptions.Item>
                  <Descriptions.Item label="目的地">{logisticsDetail.destination}</Descriptions.Item>
                  <Descriptions.Item label="寄件时间">{logisticsDetail.send_time}</Descriptions.Item>
                  <Descriptions.Item label="签收状态"><Tag color={logisticsDetail.sign_status === '正常签收' ? '#3F8600' : '#EF9F27'}>{logisticsDetail.sign_status}</Tag></Descriptions.Item>
                  <Descriptions.Item label="包裹尺寸">{logisticsDetail.package_length}×{logisticsDetail.package_width}×{logisticsDetail.package_height} cm</Descriptions.Item>
                  <Descriptions.Item label="重量">{logisticsDetail.weight} kg</Descriptions.Item>
                  <Descriptions.Item label="收件人">{logisticsDetail.receiver_name}</Descriptions.Item>
                  <Descriptions.Item label="揽件业务员">{logisticsDetail.courier_name}</Descriptions.Item>
                </Descriptions>
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        title={`管理快递单号 - ${trackingModal?.platform_order_no || ''}`}
        open={!!trackingModal}
        onCancel={() => setTrackingModal(null)}
        footer={null}
        width={480}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: '#8c8c8c', marginBottom: 6 }}>该订单的全部发货快递单号（蓝色为主单号，其余为手动录入的额外单号）：</div>
          <Space size={4} wrap>
            {trackingModal && <Tag color="#378ADD">{trackingModal.tracking_no}（主单号）</Tag>}
            {(trackingModal?.extra_tracking_nos || []).map(t => (
              <Tag key={t} closable onClose={() => removeExtraTracking(t)}>{t}</Tag>
            ))}
          </Space>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input placeholder="输入额外快递单号，回车或点添加" value={newTracking} onChange={e => setNewTracking(e.target.value)} onPressEnter={addExtraTracking} />
          <Button type="primary" icon={<PlusOutlined />} onClick={addExtraTracking}>添加</Button>
        </div>
      </Modal>

      <Modal
        title="导入异常报告"
        open={!!anomalyModal}
        onCancel={() => setAnomalyModal(null)}
        footer={[<Button key="ok" type="primary" onClick={() => setAnomalyModal(null)}>知道了</Button>]}
        width={720}
      >
        <p style={{ color: '#cf1322', marginTop: 0, fontWeight: 500 }}>
          以下 {anomalyModal ? anomalyModal.length : 0} 个订单存在数据异常，已在订单列表标色提示，建议核对：
        </p>
        <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', width: 220 }}>订单号</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>异常项</th>
              </tr>
            </thead>
            <tbody>
              {(anomalyModal || []).map((a, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '8px', verticalAlign: 'top' }}>
                    {a.level === 'red' && <Tag color="#CF1322">严重</Tag>}
                    {a.level === 'purple' && <Tag color="#722ED1">未登记</Tag>}
                    {a.level === 'yellow' && <Tag color="#FAAD14">警告</Tag>}
                    {a.order_no}
                  </td>
                  <td style={{ padding: '8px', color: '#595959' }}>{(a.anomalies || []).join('；')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>

      <Modal
        title="上传发货订单"
        open={uploadModal}
        onCancel={() => !uploading && setUploadModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setUploadModal(false)} disabled={uploading}>取消</Button>,
          <Button key="ok" type="primary" loading={uploading} onClick={handleUpload}>
            开始导入
          </Button>,
        ]}
        width={720}
      >
        <Segmented
          value={importChannel}
          onChange={setImportChannel}
          options={[
            { label: '拼多多 / 微信订单', value: 'pdd' },
            { label: '淘宝发货单', value: 'taobao' },
          ]}
          style={{ marginBottom: 12 }}
        />
        <Segmented
          value={importMode}
          onChange={setImportMode}
          options={[
            { label: '每日导入（扣库存）', value: 'daily' },
            { label: '历史补录（不扣库存）', value: 'history' },
          ]}
          style={{ marginBottom: 12 }}
        />
        {importChannel === 'taobao' ? (
          <p style={{ color: '#8B6914', marginTop: 0, background: 'var(--brand-bg)', padding: '8px 12px', borderRadius: 8 }}>
            <b>淘宝发货单</b>：直接上传淘宝后台导出的「发货单」Excel，系统按列名自动识别（订单号/商品规格/实付金额/快递单号/发货时间），
            并按「商品规格 → 系统规格编码」映射自动匹配成本、运费、利润，渠道记为「淘宝」。{importMode === 'history' ? '历史补录模式跳过库存扣减。' : '导入即出库扣库存。'}
          </p>
        ) : importMode === 'daily' ? (
          <p style={{ color: '#8c8c8c', marginTop: 0 }}>
            上传每日发货订单 Excel（含「发货订单」「Sheet1 物流明细」两个工作表）。系统将按订单号自动新增或更新，
            并从<b>商品成本管理</b>页同步成本、运费、辅材，自动重算每日发货利润。
          </p>
        ) : (
          <p style={{ color: '#8B6914', marginTop: 0, background: 'var(--brand-bg)', padding: '8px 12px', borderRadius: 8 }}>
            <b>历史补录模式</b>：用于补录 3-7 月等历史订单，<b>跳过库存扣减</b>（当前库存已是实际值，再扣会重复）。
            系统仍会按「发货时间」自动归月，并按商品主数据同步成本、运费、辅材，自动重算利润。
          </p>
        )}
        <Dragger
          accept=".xlsx,.xls"
          maxCount={1}
          beforeUpload={(file) => { setUploadFile(file); return false; }}
          onRemove={() => setUploadFile(null)}
          fileList={uploadFile ? [{ uid: '1', name: uploadFile.name, status: 'done' }] : []}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">点击或拖拽 Excel 文件到此区域</p>
          <p className="ant-upload-hint">支持 .xlsx / .xls 格式</p>
        </Dragger>

        {uploadResult && (
          <div style={{ marginTop: 16 }}>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={8}><Card size="small"><Statistic title="新增订单" value={uploadResult.newOrders} suffix="单" /></Card></Col>
              <Col span={8}><Card size="small"><Statistic title="更新订单" value={uploadResult.updatedOrders} suffix="单" /></Card></Col>
              <Col span={8}><Card size="small"><Statistic title="订单总数" value={uploadResult.totalOrders} suffix="单" /></Card></Col>
            </Row>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>每日发货利润核算</div>
            <Table
              size="small"
              pagination={false}
              dataSource={(uploadResult.daily || []).map((d, i) => ({ key: i, ...d }))}
              columns={[
                { title: '发货日期', dataIndex: 'shipping_date' },
                { title: '订单数', dataIndex: 'order_count', align: 'right' },
                { title: '营收', dataIndex: 'revenue', align: 'right', render: v => `¥${yuan(v)}` },
                { title: '成本', dataIndex: 'cost', align: 'right', render: v => `¥${yuan(v)}` },
                { title: '运费', dataIndex: 'shipping', align: 'right', render: v => `¥${yuan(v)}` },
                { title: '辅材', dataIndex: 'packaging', align: 'right', render: v => `¥${yuan(v)}` },
                { title: '利润', dataIndex: 'profit', align: 'right',
                  render: v => <span style={{ color: v >= 0 ? '#CF1322' : '#3F8600', fontWeight: 600 }}>¥{yuan(v)}</span> },
              ]}
            />
          </div>
        )}
      </Modal>

      {/* 手动录入订单（微信客户，共用库存） */}
      <Modal title={<span><PlusOutlined style={{ color: 'var(--brand)' }} /> 手动录入订单（微信/淘宝客户）</span>}
        open={manualModal} onOk={submitManual} onCancel={() => setManualModal(false)}
        okText="保存订单" cancelText="取消" confirmLoading={manualSaving} width={560} destroyOnClose>
        <Form form={manualForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="spec_codes" label="商品规格编码（可多选）" rules={[{ required: true, message: '请选择商品' }]}
            extra="选择商品成本页已登记的编码；多选表示一单多件（共用库存，自动扣减）">
            <Select mode="multiple" showSearch placeholder="搜索并选择商品编码" options={prodOpts}
              filterOption={(i, o) => (o.label || '').includes(i)} maxTagCount="responsive" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="piece_qty" label="件数（每编码）" rules={[{ required: true, message: '请填件数' }]}>
                <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="默认 1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="order_price" label="实收金额（元）" rules={[{ required: true, message: '请填实收金额' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="客户实付" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="shipping_date" label="发货日期" rules={[{ required: true, message: '请选日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shop" label="店铺/渠道">
                <Select options={[{ value: '微信客户', label: '微信客户' }, { value: '淘宝客户', label: '淘宝客户' }, ...((filterOptions.shops || []).map(s => ({ value: s, label: s })))]} placeholder="默认 微信客户" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="region" label="收货省份（用于自动算运费）">
                <Input placeholder="如：浙江 / 江苏" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="courier_company" label="快递公司">
                <Input placeholder="如：极兔 / 中通" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="shipping_mode" label="运费方式" initialValue="auto" style={{ marginBottom: 12 }}>
            <Segmented
              options={[
                { label: '自动计算（按重量+区域）', value: 'auto' },
                { label: '手动填写', value: 'manual' },
              ]}
            />
          </Form.Item>
          {shippingMode === 'manual' && (
            <Form.Item name="manual_shipping" label="运费金额（元）" rules={[{ required: true, message: '请填运费' }]}
              extra="客户自付运费请填 0（运费记 0，不计入成本）">
              <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0 = 客户付运费" />
            </Form.Item>
          )}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="tracking_no" label="快递单号">
                <Input placeholder="选填" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="customer_name" label="客户姓名（微信客户）">
                <Input placeholder="选填" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="收货地址">
            <Input placeholder="选填" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="选填" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 订单发货（待发货 → 已发货，扣库存 + 进财务） */}
      <Modal title={`订单发货 · ${shipTarget?.platform_order_no || ''}`}
        open={!!shipTarget} onOk={submitShip} onCancel={() => setShipTarget(null)}
        okText="确认发货" cancelText="取消" confirmLoading={shipSaving} width={480} destroyOnClose>
        <Form form={shipForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="shipping_date" label="发货日期" rules={[{ required: true, message: '请选日期' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="courier_company" label="快递公司">
            <Input placeholder="如：极兔快递" />
          </Form.Item>
          <Form.Item name="tracking_no" label="快递单号">
            <Input placeholder="选填，用于物流追踪" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
